import {
  type DependencyList,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { MIN_TABLE_SCROLL_HEIGHT } from 'utils/constants';

const BOTTOM_PADDING = 16;
/** Buffer to prevent scroll.y from toggling on/off at the overflow boundary. */
const SCROLL_HEIGHT_THRESHOLD = 2;

/**
 * Returns the scrollable body height of the table.
 *
 * Uses `.ant-table-tbody` scrollHeight so measurement is body-only in both DOM layouts:
 * scroll.y OFF (single `.ant-table-content table`) and ON (split `.ant-table-header` /
 * `.ant-table-body`). That matches `availableMaxHeight`, which already excludes the header.
 */
const getTableContentHeight = (tableContainer: HTMLElement): number | undefined => {
  const tbody = tableContainer.querySelector<HTMLElement>('.ant-table-tbody');

  if (!tbody || tbody.scrollHeight <= 0) {
    return undefined;
  }
  return tbody.scrollHeight;
};

export const resolveScrollHeight = (
  contentHeight: number,
  availableMaxHeight: number,
  currentScrollHeight: number | undefined
): number | undefined => {
  if (currentScrollHeight !== undefined) {
    if (contentHeight <= availableMaxHeight - SCROLL_HEIGHT_THRESHOLD) {
      return undefined;
    }
    if (contentHeight > availableMaxHeight) {
      return availableMaxHeight;
    }
    return currentScrollHeight;
  }
  return contentHeight > availableMaxHeight ? availableMaxHeight : undefined;
};

export const useTableScrollHeight = (
  tableRef: RefObject<HTMLElement>,
  deps: DependencyList = []
) => {
  const [scrollHeight, setScrollHeight] = useState<number>();
  const scrollHeightRef = useRef<number | undefined>(undefined);

  const calculateScrollHeight = useCallback(() => {
    const tableContainer = tableRef.current;
    if (!tableContainer) {
      return;
    }

    const { top } = tableContainer.getBoundingClientRect();

    const cardHeader = tableContainer.querySelector<HTMLElement>('.ant-card-head');
    const tableHeader = tableContainer.querySelector<HTMLElement>('.ant-table-thead');
    const pagination = tableContainer.querySelector<HTMLElement>('.ant-pagination');

    const paginationStyle = pagination ? window.getComputedStyle(pagination) : null;
    const paginationMargin = paginationStyle
      ? parseFloat(paginationStyle.marginTop) + parseFloat(paginationStyle.marginBottom)
      : 0;

    const availableMaxHeight = Math.max(
      MIN_TABLE_SCROLL_HEIGHT,
      window.innerHeight -
        // Use the absolute viewport offset so available height remains stable when the table
        // is scrolled past the top of the window and getBoundingClientRect().top becomes negative.
        Math.abs(top) -
        (cardHeader?.offsetHeight ?? 0) -
        (tableHeader?.offsetHeight ?? 0) -
        (pagination?.offsetHeight ?? 0) -
        paginationMargin -
        BOTTOM_PADDING
    );

    const contentHeight = getTableContentHeight(tableContainer);
    if (contentHeight === undefined) {
      // Preserve scroll height during transient DOM states (loading, remount, scroll.y layout).
      return;
    }

    const nextScrollHeight = resolveScrollHeight(
      contentHeight,
      availableMaxHeight,
      scrollHeightRef.current
    );
    if (nextScrollHeight === scrollHeightRef.current) {
      return;
    }

    scrollHeightRef.current = nextScrollHeight;
    setScrollHeight(nextScrollHeight);
  }, [tableRef]);
  useLayoutEffect(() => {
    const tableContainer = tableRef.current;
    if (!tableContainer) {
      return;
    }

    let rafId: number | null = null;
    let observedTbody: Element | null = null;
    const mutationObserver = new MutationObserver(() => {
      scheduleCalculateAndBind();
    });

    const bindTbodyObserver = () => {
      const tbody = tableContainer.querySelector('.ant-table-tbody');
      if (tbody === observedTbody) {
        return;
      }
      mutationObserver.disconnect();
      observedTbody = tbody;
      if (tbody) {
        mutationObserver.observe(tbody, { childList: true, subtree: true });
      }
    };

    const scheduleCalculateAndBind = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        bindTbodyObserver();
        calculateScrollHeight();
      });
    };

    bindTbodyObserver();
    calculateScrollHeight();

    window.addEventListener('resize', scheduleCalculateAndBind);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      mutationObserver.disconnect();
      window.removeEventListener('resize', scheduleCalculateAndBind);
    };
    // Caller-supplied deps (e.g. `loading`) extend this array so layout is remeasured
    // after async table updates. tableRef is stable; calculateScrollHeight already reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateScrollHeight, ...deps]);

  return scrollHeight;
};
